import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Printer, Download } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Same fallback strategy as MenuPreview so every cell renders even if an
// image is missing.
const FALLBACK_BY_CATEGORY = {
  combos: "/combos/btw-super-combo.jpg",
  burgers: "/combos/btw-slider-combo.jpg",
  "taco-bar": "/combos/btw-3-tacos-combo.jpg",
  wings: "/catering/btw-20-wing-sampler.jpg",
  seafood: "/combos/btw-5-fries.jpg",
  "sides-soups-salads": "/combos/btw-5-fries.jpg",
  drinks: "/hero/btw-spread.jpg",
  dessert: "/catering/carrot-cake-14-slices-8b3dea42.jpg",
  "family-meals": "/combos/btw-super-combo.jpg",
  specials: "/combos/btw-3-tacos-combo.jpg",
};
const GLOBAL_FALLBACK = "/hero/btw-spread.jpg";

function resolveImg(item, categoryId) {
  const raw = (item?.img || "").trim();
  if (raw) return raw;
  return FALLBACK_BY_CATEGORY[categoryId] || GLOBAL_FALLBACK;
}

export default function PrintMenu() {
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("image"); // "image" | "text" | "compact"

  // Cycle: image -> compact -> text -> image
  const nextMode = () => {
    setMode((m) => (m === "image" ? "compact" : m === "compact" ? "text" : "image"));
  };
  const modeLabel = {
    image: "with images",
    compact: "1-page · with images",
    text: "text only",
  }[mode];
  const nextModeLabel = {
    image: "1-Page Compact",
    compact: "Text Only",
    text: "Show Full Images",
  }[mode];

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/menu`);
        if (alive) setMenu(data);
      } catch (e) {
        console.error("[print-menu] failed to load menu", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const handlePrint = () => window.print();
  const handleDownloadZip = () => {
    window.location.href = `${API}/print/menu-images.zip`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-black">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (!menu) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-black">
        <p>Menu unavailable. Try again.</p>
      </div>
    );
  }

  return (
    <div className="bg-white text-black min-h-screen print-menu-root" data-testid="print-menu-page">
      {/* Floating toolbar — hidden when printing */}
      <div className="no-print sticky top-0 z-20 bg-white border-b border-black/10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-3 flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-2">
            <span className="font-black tracking-tight text-lg">BTW Print Menu</span>
            <span className="text-xs uppercase tracking-widest text-black/50">
              {modeLabel}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={nextMode}
              data-testid="print-menu-toggle-mode"
              className="px-4 py-2 text-sm font-bold uppercase tracking-wider border-2 border-black hover:bg-black hover:text-white transition"
            >
              {nextModeLabel}
            </button>
            <button
              type="button"
              onClick={handleDownloadZip}
              data-testid="print-menu-download-zip"
              className="px-4 py-2 text-sm font-bold uppercase tracking-wider bg-[#5FBDD6] text-black hover:brightness-95 transition inline-flex items-center gap-2"
            >
              <Download className="w-4 h-4" /> Download Images ZIP
            </button>
            <button
              type="button"
              onClick={handlePrint}
              data-testid="print-menu-print"
              className="px-4 py-2 text-sm font-bold uppercase tracking-wider bg-black text-white hover:bg-black/80 transition inline-flex items-center gap-2"
            >
              <Printer className="w-4 h-4" /> Print
            </button>
          </div>
        </div>
      </div>

      {/* Printable canvas */}
      <div className={`print-canvas mx-auto py-10 ${mode === "compact" ? "compact-canvas px-4 sm:px-6 max-w-[1600px]" : "max-w-[1400px] px-6 sm:px-10"}`}>
        <header className={`text-center border-b-4 border-black ${mode === "compact" ? "mb-4 pb-3" : "mb-10 pb-6"}`}>
          <div className={`uppercase tracking-[0.4em] text-[#5FBDD6] font-bold ${mode === "compact" ? "text-[9px] mb-1" : "text-[11px] mb-2"}`}>
            Jamaica, Queens · 718-555-BTW
          </div>
          <h1 className={`font-black tracking-tight leading-none ${mode === "compact" ? "text-4xl" : "text-5xl sm:text-6xl"}`}>
            BTW
          </h1>
          <div className={`font-semibold tracking-[0.3em] uppercase ${mode === "compact" ? "mt-1 text-xs" : "mt-2 text-base sm:text-lg"}`}>
            Burgers · Tacos · Wings
          </div>
          {mode !== "compact" && (
            <p className="mt-4 text-xs sm:text-sm text-black/60 max-w-xl mx-auto">
              Bold flavor, made fresh, built for you. Catering · Pickup · Delivery.
            </p>
          )}
        </header>

        {mode === "compact" ? (
          <div className="compact-columns" data-testid="print-menu-compact">
            {menu.categories.map((cat) => (
              <section key={cat.id} className="compact-section">
                <h2 className="compact-cat-label">{cat.label}</h2>
                <div className="compact-items">
                  {cat.items.map((item, idx) => (
                    <div className="compact-item" key={`${cat.id}-c-${idx}`}>
                      <div className="compact-thumb">
                        <img
                          src={resolveImg(item, cat.id)}
                          alt={item.name}
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.src =
                              FALLBACK_BY_CATEGORY[cat.id] || GLOBAL_FALLBACK;
                          }}
                        />
                      </div>
                      <div className="compact-body">
                        <div className="compact-name">{item.name}</div>
                        <div className="compact-price">{item.price}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          menu.categories.map((cat) => (
          <section key={cat.id} className="mb-12 print-section">
            <div className="flex items-baseline gap-4 mb-5 border-b-2 border-black pb-2">
              <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-tight">
                {cat.label}
              </h2>
              <span className="text-[10px] uppercase tracking-[0.3em] text-black/50">
                {cat.items.length} item{cat.items.length === 1 ? "" : "s"}
              </span>
            </div>

            {mode === "image" ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                {cat.items.map((item, idx) => (
                  <article
                    key={`${cat.id}-${idx}`}
                    className="print-card break-inside-avoid"
                  >
                    <div className="aspect-square w-full overflow-hidden bg-neutral-100 border border-black/10">
                      <img
                        src={resolveImg(item, cat.id)}
                        alt={item.name}
                        loading="lazy"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.src =
                            FALLBACK_BY_CATEGORY[cat.id] || GLOBAL_FALLBACK;
                        }}
                      />
                    </div>
                    <div className="pt-2">
                      <div className="flex items-baseline justify-between gap-2">
                        <h3 className="font-bold text-sm leading-tight">
                          {item.name}
                        </h3>
                        <span className="font-black text-sm whitespace-nowrap">
                          {item.price}
                        </span>
                      </div>
                      {item.desc ? (
                        <p className="text-[11px] text-black/65 leading-snug mt-1">
                          {item.desc}
                        </p>
                      ) : null}
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-2">
                {cat.items.map((item, idx) => (
                  <div
                    key={`${cat.id}-text-${idx}`}
                    className="flex items-start justify-between gap-3 py-2 border-b border-dotted border-black/20 break-inside-avoid"
                  >
                    <div className="min-w-0">
                      <div className="font-bold text-sm">{item.name}</div>
                      {item.desc ? (
                        <div className="text-[11px] text-black/65 leading-snug">
                          {item.desc}
                        </div>
                      ) : null}
                    </div>
                    <div className="font-black text-sm whitespace-nowrap">
                      {item.price}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
          ))
        )}

        <footer className={`pt-6 border-t-2 border-black text-center text-xs text-black/60 ${mode === "compact" ? "mt-4" : "mt-12"}`}>
          <div className="font-bold tracking-widest uppercase text-black">
            btwnyc.com · @btwnyc
          </div>
          <div className="mt-1">
            Prices and availability subject to change. Closed Mondays.
          </div>
        </footer>
      </div>

      <style>{`
        .print-menu-root { font-family: 'Inter', system-ui, -apple-system, sans-serif; }
        .print-card img { image-rendering: -webkit-optimize-contrast; }

        /* --- Compact (1-page) mode --- */
        .compact-columns {
          column-count: 3;
          column-gap: 18px;
        }
        .compact-section {
          break-inside: avoid;
          page-break-inside: avoid;
          margin-bottom: 8px;
          display: inline-block;   /* needed for column-break behavior */
          width: 100%;
        }
        .compact-cat-label {
          font-size: 10px;
          font-weight: 900;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          border-bottom: 1.5px solid #000;
          padding-bottom: 2px;
          margin-bottom: 4px;
          color: #000;
        }
        .compact-items {
          display: flex;
          flex-direction: column;
          gap: 3px;
        }
        .compact-item {
          display: flex;
          align-items: center;
          gap: 6px;
          break-inside: avoid;
          page-break-inside: avoid;
        }
        .compact-thumb {
          flex: 0 0 32px;
          width: 32px;
          height: 32px;
          overflow: hidden;
          background: #f5f5f5;
          border: 1px solid rgba(0,0,0,0.08);
          border-radius: 3px;
        }
        .compact-thumb img {
          width: 100%; height: 100%; object-fit: cover; display: block;
        }
        .compact-body {
          flex: 1 1 auto;
          min-width: 0;
          display: flex;
          align-items: baseline;
          gap: 4px;
          line-height: 1.1;
        }
        .compact-name {
          font-size: 9px;
          font-weight: 700;
          flex: 1 1 auto;
          min-width: 0;
          color: #000;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .compact-price {
          font-size: 9px;
          font-weight: 900;
          white-space: nowrap;
          color: #000;
        }

        /* Tighten on small viewports — give the preview some breathing room */
        @media (max-width: 900px) {
          .compact-columns { column-count: 2; }
        }
        @media (max-width: 500px) {
          .compact-columns { column-count: 1; }
        }

        @media print {
          @page { size: 11in 17in; margin: 0.35in; }
          html, body { background: #fff !important; }
          .no-print { display: none !important; }
          .print-canvas { max-width: 100% !important; padding: 0 !important; }
          .print-section { break-inside: avoid; page-break-inside: avoid; }
          .print-card { break-inside: avoid; page-break-inside: avoid; }
          /* Compact mode print: scale everything to fit one page on 11x17. */
          .compact-canvas {
            padding: 0 !important;
            font-size: 9pt;
          }
          .compact-columns { column-count: 3; column-gap: 0.25in; }
          .compact-thumb { width: 36px; height: 36px; }
          .compact-name, .compact-price { font-size: 9pt; }
          .compact-cat-label { font-size: 10pt; }
        }
      `}</style>
    </div>
  );
}
